// encounterPersistence.js
// Combat PRO - Active-combat cloud persistence medium (durability only).
//
// Stage 118 (Active Combat Cloud Persistence - portal integration).
//
// The storage MEDIUM (HOW) for encounter durability. It owns exactly two
// operations - save() and load() - and nothing else. It does NOT own WHEN to
// persist (that is the Stage 116 useEncounterPersistence runtime), nor WHAT the
// combat payload is (that is the Stage 115 captureEncounterCombat / encounter
// store engine). React (via CombatContext) wires this medium to the runtime's
// onPersist seam and calls load() at boot; it does not duplicate it.
//
// Portal integration (identical to spellPersistence.js / monsterPersistence.js):
//   The legacy battletracker.html used standalone auth (API_BASE + authFetch +
//   getAuthHeaders + bt_auth_token + bt_session_id + credentials:'include').
//   NONE of that is recreated here - those were legacy implementation details.
//   Combat PRO runs inside the authenticated VISINVOKE customer portal, whose
//   request layer is the shared axios instance in src/api.js (baseURL + HttpOnly
//   auth_token cookie via withCredentials, plus the 401/403 interceptor). This
//   module reuses that instance exactly. The only thing defined here is the
//   endpoint PATH, which mirrors the legacy contract (API_BASE was
//   /v1/battletracker) as a USER-scoped resource consistent with the existing
//   /user/spells and /user/monsters adapters, so the full path under the portal
//   baseURL is /v1/battletracker/user/encounter-state.
//
// Durability ONLY. Live synchronisation (sendActionToCloud, WebSockets,
// /session/{id}/action, /restore, /history) is a separate subsystem and is NOT
// touched here. The legacy session-scoped model (/session/{id}) and its
// bt_session_id are intentionally NOT reproduced.
//
// Payload (Stage 125A: Encounter Store Cloud Persistence): the engine's
// capturePersistPayload superset - the Stage 115 active-combat fields (monsters,
// currentRound, activeMonsterCount, currentCombatantIndex, rollHistory,
// combatLogEntries, historyStack, players) PLUS the complete 12-slot store under
// btEncounterStore (the legacy saveToCloud shape, 3891-3915), with the active
// slot's combat folded from the live capture. This module remains
// payload-AGNOSTIC: it stores and returns whatever the seam hands it, so the
// Stage 125A payload change required NO change to this medium. Rows saved by the
// earlier Stage 118 (active-combat-only) format load unchanged - the boot path
// treats a payload without btEncounterStore as a bare active combat.
// btMonsterTemplates is NOT attached (the Stage 63 template runtime owns template
// cloud persistence on /user/monsters).
//
// No localStorage. No optional chaining. No nullish coalescing. No em dash.

import API from '../../api';

// Endpoint path under the portal API baseURL. Mirrors the legacy API_BASE
// ('/v1/battletracker') as a user-scoped resource, consistent with
// USER_SPELLS_PATH / USER_MONSTERS_PATH. Auth and base URL are owned by the
// shared API instance; this is only the route.
export var ENCOUNTER_STATE_PATH = '/v1/battletracker/user/encounter-state';

// loadEncounterState - GET the current user's saved active-combat state.
// Returns the persisted combat payload (the row's .data), following the same
// row -> .data convention as loadUserSpells / fetchUserMonsters. Returns null on
// any failure or when there is nothing stored (the portal interceptor handles
// 401/403 before this catch; the shared API sends the auth cookie automatically
// via withCredentials). null means "no payload" so the boot path leaves the
// runtime at its normal default combat.
export async function loadEncounterState() {
    try {
        var res = await API.get(ENCOUNTER_STATE_PATH);
        var row = res ? res.data : null;
        if (!row) { return null; }
        // The persisted row wraps the combat payload under .data (row -> .data
        // convention). A bare payload (no wrapper) is tolerated as a fallback.
        if (row.data) { return row.data; }
        return row;
    } catch (err) {
        return null;
    }
}

// saveEncounterState - POST the active-combat state.
// Body { name, data } mirrors the saveUserSpell contract (name is a stable
// label; data is the captureEncounterCombat payload). Returns true on a 2xx
// response, false on any failure. axios resolves only on 2xx by default and sets
// JSON Content-Type for object bodies, so no manual header is needed.
export async function saveEncounterState(combat) {
    try {
        var res = await API.post(ENCOUNTER_STATE_PATH, { name: 'Active Combat', data: combat });
        return !!(res && res.status >= 200 && res.status < 300);
    } catch (err) {
        return false;
    }
}
